import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AuthQuery } from '@app/auth/auth.query';
import { Standort } from '../../../services/kontrolltermin.service';
import {
  StandortService,
  TechnikerinfoFields,
  TechnikerinfoRevision,
} from '../../../services/standort.service';

type TechnikerinfoKey = keyof TechnikerinfoFields;

@Component({
  selector: 'app-techniker-infos',
  templateUrl: './techniker-infos.component.html'
})
export class TechnikerInfosComponent implements OnChanges {
  @Input() standort?: Standort;
  @Output() saved = new EventEmitter<void>();

  /** The five editable fields, in the same order as the mobile app shows them. */
  readonly fields: { key: TechnikerinfoKey; label: string }[] = [
    { key: 'zugang', label: 'APPOINTMENT_EDIT.FIELD_ZUGANG' },
    { key: 'kontakt_text', label: 'APPOINTMENT_EDIT.FIELD_KONTAKT_OBJEKT' },
    { key: 'boxen', label: 'APPOINTMENT_EDIT.FIELD_BOXEN' },
    { key: 'objekt_notiz', label: 'APPOINTMENT_EDIT.FIELD_OBJEKT_NOTIZ' },
    { key: 'bemerkung_struktur_anmerkung', label: 'APPOINTMENT_EDIT.FIELD_BEMERKUNG_ZUTRITT' },
  ];

  editMode = false;
  saving = false;
  saveError = '';
  draft: TechnikerinfoFields = {};

  historyOpen = false;
  historyLoading = false;
  historyError = '';
  /** Oldest first, so the slider runs left (old) to right (current). */
  revisions: TechnikerinfoRevision[] = [];
  selectedIndex = 0;

  constructor(private standortService: StandortService, private authQuery: AuthQuery) {}

  ngOnChanges(): void {
    // A different Kontrolltermin (and standort) was loaded into the page.
    this.editMode = false;
    this.saveError = '';
    this.historyOpen = false;
    this.revisions = [];
  }

  /** Same gate as the header-info edit button: SuperUser, CompAdmin, Büro. */
  get canEdit(): boolean {
    const role = this.authQuery.role;
    return role === 1 || role === 2 || role === 11; // 1 = SuperUser, 2 = CompAdmin, 11 = Büro
  }

  get lastRevision(): TechnikerinfoRevision | null {
    return this.standort?.latest_technikerinfo_revision || null;
  }

  /** True while an older version is being previewed instead of the current text. */
  get isPreviewingOldVersion(): boolean {
    return this.historyOpen && this.revisions.length > 0 && this.selectedIndex < this.revisions.length - 1;
  }

  get selectedRevision(): TechnikerinfoRevision | null {
    return this.revisions[this.selectedIndex] || null;
  }

  /** Standort technician/access info (BMD Kopftext import + legacy zutritt remark). */
  hasTechnikerInfos(): boolean {
    return this.fields.some((f) => !!(this.displayValue(f.key) || '').trim());
  }

  /** What to render per field: the previewed revision when stepping back, else the live value. */
  displayValue(key: TechnikerinfoKey): string {
    if (this.isPreviewingOldVersion) {
      return (this.selectedRevision?.[key] as string) || '';
    }
    return ((this.standort as any)?.[key] as string) || '';
  }

  startEdit(): void {
    if (!this.standort) {
      return;
    }
    this.draft = {};
    this.fields.forEach((f) => (this.draft[f.key] = this.displayValue(f.key)));
    this.saveError = '';
    this.editMode = true;
  }

  cancelEdit(): void {
    this.editMode = false;
    this.saveError = '';
    this.draft = {};
  }

  save(): void {
    if (!this.standort || this.saving) {
      return;
    }
    this.saving = true;
    this.saveError = '';

    this.standortService.updateTechnikerinfos(this.standort.id, this.draft).subscribe(
      (response) => {
        if (response.result !== 1) {
          this.saveError = 'Speichern fehlgeschlagen.';
          this.saving = false;
          return;
        }
        // Reflect the saved text and the new "last changed" line without a reload.
        this.fields.forEach((f) => ((this.standort as any)[f.key] = (response.data as any)?.[f.key] ?? null));
        if (this.standort) {
          this.standort.latest_technikerinfo_revision = response.data?.latest_technikerinfo_revision || null;
        }
        this.saving = false;
        this.editMode = false;
        this.draft = {};
        this.saved.emit();
        if (this.historyOpen) {
          this.loadHistory();
        }
      },
      (err) => {
        this.saveError = err?.error?.error || err?.message || 'Speichern fehlgeschlagen.';
        this.saving = false;
      }
    );
  }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
    if (this.historyOpen && this.revisions.length === 0) {
      this.loadHistory();
    }
    if (!this.historyOpen) {
      this.selectedIndex = Math.max(0, this.revisions.length - 1);
    }
  }

  onVersionChange(value: string | number): void {
    this.selectedIndex = typeof value === 'string' ? parseInt(value, 10) : value;
  }

  /**
   * Put the previewed version back as the current text. It is saved like any
   * other edit, so it becomes a new revision and nothing is lost.
   */
  restoreSelected(): void {
    const revision = this.selectedRevision;
    if (!revision || this.saving) {
      return;
    }
    this.draft = {};
    this.fields.forEach((f) => (this.draft[f.key] = (revision[f.key] as string) || ''));
    this.save();
  }

  private loadHistory(): void {
    if (!this.standort) {
      return;
    }
    this.historyLoading = true;
    this.historyError = '';

    this.standortService.getTechnikerinfoRevisions(this.standort.id).subscribe(
      (response) => {
        // API returns newest first; the slider reads oldest to newest.
        this.revisions = (response.data || []).slice().reverse();
        this.selectedIndex = Math.max(0, this.revisions.length - 1);
        this.historyLoading = false;
      },
      (err) => {
        this.historyError = err?.error?.error || err?.message || 'Verlauf konnte nicht geladen werden.';
        this.historyLoading = false;
      }
    );
  }
}
